import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { AcornsIcon, Icon } from '@acorns/icons'
import { useRemoteStrings } from '@acorns/web-utils'
import { compose } from 'recompose'

import getDefaultEventData from 'components/Trackers/defaults'
import { InvestmentProduct } from 'generated/types'
import { CalculatingPortfolioLoading } from 'pages/PortfolioTheme/calculating-portfolio-loading'
import { CalculatingPortfolioSuccess } from 'pages/PortfolioTheme/calculating-portfolio-success'
import trackEvent from 'utils/analytics/trackEvent'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'

import BitcoinImage from './assets/bitcoin.svg'
import { CryptoLander } from './CryptoLander'
import { BitcoinLanderButtonTapped, BitcoinLanderModalButtonTapped } from './events'
import { StyledValueProp, ValuePropText } from './styled-components'

const enhance = compose(withFlowRouter, withOptimizely)

export const STRING_PREFIX = 'invest.new_etf.lander.'
const stringKeys = [
  'action_strip_title',
  'alert.add_to_portfolio.cta.cancel',
  'alert.add_to_portfolio.cta.confirm',
  'alert.add_to_portfolio.title',
  'body1',
  'body2.markdown',
  'cta',
  'header1',
  'header2',
  'loading.label',
  'success.body',
  'success.cta',
  'success.body.recurring',
  'success.body.one_time',
  'success.cta.one_time',
  'success.cta.recurring',
  'success.cta.alt',
  'success.title.markdown',
  'title',
  'value_prop1',
  'value_prop2',
  'value_prop3',
  'disclosure.link.markdown',
] as const
const iconKeys = [Icon.CryptoWhite, Icon.TransactionWhite, Icon.BinocularWhite]
type ValuesOf<T extends readonly any[]> = T[number]

type PageCopy = {
  [key in ValuesOf<typeof stringKeys>]: string
}
type Props = WithFlowRouterProps & WithOptimizelyProps

export const ValueProp = ({ copy, iconName }: { copy: string; iconName: Icon }) => (
  <StyledValueProp>
    <AcornsIcon icon={iconName} height={24} width={24} />
    <ValuePropText>{copy}</ValuePropText>
  </StyledValueProp>
)

export type CryptoInterstitalProps = {
  onBack: () => void
  imageUrl: string
  icons: Icon[]
  toggleModal?: () => void
  isModalOpen: boolean
  onAddPortfolioConfirmPress: () => void
  investmentProduct: string
  onAddPortfolioPress: (cb?: () => void) => void
  recommendedRisk: string
} & PageCopy

type LocationState<T extends { [key: string]: string }> = {
  state?: T
}

export const CryptoInterstitialComponent: React.FC<Props> = ({ route, trackOptimizely }) => {
  const location = useLocation<LocationState<{ formattedRisk: string }>>()

  const investmentProduct = InvestmentProduct.Invest.toLowerCase()
  const [isAddingBitcoin, setIsAddingBitcoin] = useState(false)
  const [isAddingBitcoinSuccess, setIsAddingBitcoinSuccess] = useState(false)
  const [addedBitcoin, setAddedBitcoin] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { getRemoteString } = useRemoteStrings()

  const recommendedRisk = location?.state?.formattedRisk

  const fetchedStrings = stringKeys.map(str => getRemoteString({ key: `${STRING_PREFIX}${str}` }))

  const strings = fetchedStrings.reduce((pageCopy, currentString, index) => {
    pageCopy[stringKeys[index]] = currentString
    return pageCopy
  }, {} as PageCopy)

  const onBack = () => {
    setIsModalOpen(false)
    route({
      from: FlowRoute.CryptoLander,
      action: 'update_crypto_portfolio',
      locationState: { backFromCrypto: true },
    })
  }

  const toggleModal = () => {
    setIsModalOpen(prevIsModalOpen => !prevIsModalOpen)
  }

  const onAddPortfolioPress = () => {
    trackEvent('Button Tapped', {
      eventData: BitcoinLanderButtonTapped(investmentProduct),
      ...getDefaultEventData('Button Tapped'),
    })
    setIsModalOpen(prevIsModalOpen => !prevIsModalOpen)
  }

  const onAddPortfolioConfirmPress = async () => {
    setIsModalOpen(false)
    trackEvent('Button Tapped', {
      eventData: BitcoinLanderModalButtonTapped(investmentProduct),
      ...getDefaultEventData('Button Tapped'),
    })
    trackOptimizely('bitcoin_added_to_portfolio')
    setIsAddingBitcoin(true)
  }

  // This is a fake loader
  useEffect(() => {
    if (isAddingBitcoin) {
      setTimeout(() => {
        setIsAddingBitcoin(false)
        setIsAddingBitcoinSuccess(true)
      }, 3000)
    }
  }, [isAddingBitcoin])

  useEffect(() => {
    if (isAddingBitcoinSuccess) {
      setTimeout(() => {
        setIsAddingBitcoinSuccess(false)
        setAddedBitcoin(true)
      }, 1000)
    }
  }, [isAddingBitcoinSuccess])

  if (addedBitcoin) {
    route({
      from: FlowRoute.CryptoLander,
      action: 'update_crypto_portfolio',
      locationState: { isBitcoinFlow: true },
    })
  }
  return (
    <>
      {isAddingBitcoin && <CalculatingPortfolioLoading isAddingBitcoin={isAddingBitcoin} />}
      {isAddingBitcoinSuccess && <CalculatingPortfolioSuccess />}
      {!isAddingBitcoin && !isAddingBitcoinSuccess && (
        <CryptoLander
          icons={iconKeys}
          imageUrl={BitcoinImage}
          onBack={onBack}
          toggleModal={toggleModal}
          isModalOpen={isModalOpen}
          onAddPortfolioConfirmPress={onAddPortfolioConfirmPress}
          investmentProduct={investmentProduct}
          onAddPortfolioPress={onAddPortfolioPress}
          recommendedRisk={recommendedRisk}
          {...strings}
        />
      )}
    </>
  )
}

export const CryptoInterstitial = enhance(CryptoInterstitialComponent)
